// The notification owner (refresh_plan.md 3.1.7).
//
// The product has nine unrelated ways to tell a user something happened: a
// page-top lw-notice in six flavours, an unstyled black `Error: ...` div from
// errorMessage.vue, a native alert() used as field validation
// (TrucksCreate.vue:88), two window.confirm() destructive gates, and several
// silent no-ops. The plan collapses those to three surfaces: an inline field
// error (LwField), a page notice (LwNotice), and a transient toast. This module
// owns the third one, so a toast has exactly one queue and exactly one region
// reading it.
//
// It is a namespaced Vuex module rather than an event bus on purpose: the bus
// pattern is already in the codebase (Dispatch and Billing each hand a private
// emitter to their lists) and a bus has no state anyone can inspect. A module
// has one state, is inspectable, and is testable without mounting anything.
//
// Timers live here rather than in the region component because a toast must
// survive the region being re-rendered, and because a component that owns its
// own dismissal timer cannot be tested without fake timers in every suite that
// touches it.

let nextId = 1;

/** Reset between tests. Never called by application code. */
export function __resetToastIds() {
  nextId = 1;
}

export const TOAST_TONES = ['success', 'warning', 'danger', 'info', 'neutral'];

// A danger toast does not auto-dismiss. Everything else does. The reason is the
// same one that gives LwNotice role="alert" only for danger: a failure that
// vanishes on a timer is a failure the operator can miss entirely, and the one
// class of message in this product that must never be missed is "the office did
// not get it".
export const DEFAULT_TIMEOUT_MS = 6000;

const toasts = {
  namespaced: true,

  state: () => ({
    items: [],
  }),

  getters: {
    items(state) {
      return state.items;
    },
    // The region announces politely for everything except danger, which
    // interrupts. Kept in the store so the region has no policy of its own.
    hasAssertive(state) {
      return state.items.some(t => t.tone === 'danger');
    },
  },

  mutations: {
    push(state, toast) {
      state.items.push(toast);
    },
    dismiss(state, id) {
      state.items = state.items.filter(t => t.id !== id);
    },
    clear(state) {
      state.items = [];
    },
  },

  actions: {
    /**
     * @param {{ message: string, tone?: string, timeout?: number|null }} toast
     * `timeout: null` pins the toast until it is dismissed. A danger toast is
     * pinned by default.
     */
    notify({ commit }, { message, tone = 'info', timeout } = {}) {
      if (!message) {
        // A toast with no message is a bug in the caller, and rendering an
        // empty box would hide it. Fail loudly in development, do nothing in
        // production rather than throwing inside a success path.
        // eslint-disable-next-line no-console -- a silent empty toast is worse
        console.warn('lw toast: notify() called with no message; ignoring');
        return null;
      }
      const resolvedTone = TOAST_TONES.includes(tone) ? tone : 'info';
      const resolvedTimeout =
        timeout === undefined ? (resolvedTone === 'danger' ? null : DEFAULT_TIMEOUT_MS) : timeout;
      const id = nextId++;
      commit('push', { id, message, tone: resolvedTone, timeout: resolvedTimeout });
      if (resolvedTimeout) {
        setTimeout(() => commit('dismiss', id), resolvedTimeout);
      }
      return id;
    },
    dismiss({ commit }, id) {
      commit('dismiss', id);
    },
    clear({ commit }) {
      commit('clear');
    },
  },
};

export default toasts;
